import { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Input } from '../../../components/form/Input';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { useCollectionsSettings, useUpdateCollectionsSettings } from './useCollectionsSettings';
import type { CollectionsSettingsDto } from '../../../types/api';

export function CollectionsSettingsPage(): JSX.Element {
  const query = useCollectionsSettings();
  const update = useUpdateCollectionsSettings();

  const [form, setForm] = useState<CollectionsSettingsDto>({
    scrap_price_per_tonne: null,
    auto_approve_receipts: false,
  });

  useEffect(() => {
    if (!query.data) return;
    setForm(query.data);
  }, [query.data]);

  if (query.isLoading || !query.data) {
    return (
      <div>
        <PageHeader title="Settings" />
        <Card>
          <Spinner />
        </Card>
      </div>
    );
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    update.mutate(form);
  }

  return (
    <div>
      <PageHeader
        title="Settings"
        subtitle="Configure scrap pricing and receipt behaviour for Collections."
      />

      <form onSubmit={handleSubmit}>
        <Card title="Scrap pricing">
          <Input
            label="Default scrap price per tonne (£/t)"
            variant="number"
            min={0}
            step="0.01"
            value={form.scrap_price_per_tonne ?? ''}
            onChange={(e) => {
              const raw = e.target.value;
              setForm((f) => ({
                ...f,
                scrap_price_per_tonne: raw === '' ? null : Number(raw),
              }));
            }}
            helper="Used to calculate scrap car quotes. Can be adjusted per quote."
          />
        </Card>

        <Card title="Receipts">
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={form.auto_approve_receipts}
              onChange={(e) =>
                setForm((f) => ({ ...f, auto_approve_receipts: e.target.checked }))
              }
            />
            <span>
              Auto-approve receipt submissions — submitted receipts skip the
              pending-review queue and are approved automatically.
            </span>
          </label>
        </Card>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <Button type="submit" loading={update.isPending}>
            <Save size={16} />
            Save settings
          </Button>
        </div>
      </form>
    </div>
  );
}
